import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

const QueriesCRUD = () => {
  const [queries, setQueries] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 1,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchQueries();
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const fetchQueries = async (page = 1) => {
    try {
      setLoading(true);
      let url = `${BASE_URI}/queries?page=${page}&limit=${pagination.page_size}`;
      if (searchTerm.trim())
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;

      const res = await axios.get(url, getAuthHeaders());
      if (res.data?.data) {
        let data = res.data.data.map((item) => ({
          id: item.id,
          name: item.data.name || "N/A",
          email: item.data.email || "N/A",
          mobile: item.data.mobile || "N/A",
          message: item.data.message || "No message",
          status: item.data.status || "new",
          created_at: item.data.created_at || "Unknown",
        }));
        if (statusFilter !== "all")
          data = data.filter((q) => q.status.toLowerCase() === statusFilter);
        data.sort((a, b) => {
          if (a.status === "new" && b.status !== "new") return -1;
          if (b.status === "new" && a.status !== "new") return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setQueries(data);
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      } else {
        setQueries([]);
      }
      setError("");
    } catch {
      setError("Failed to fetch queries");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("status", newStatus);
      await axios.put(`${BASE_URI}/queries/${id}`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Status updated!");
      fetchQueries(pagination.page);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const deleteQuery = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(`${BASE_URI}/queries/${deleteId}`, getAuthHeaders());
      toast.success("Deleted successfully!");
      setDeleteId(null);
      const remaining = queries.length - 1;
      fetchQueries(
        remaining === 0 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page,
      );
    } catch {
      toast.error("Failed to delete.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const getNextStatus = (s) =>
    ({ new: "pending", pending: "resolved", resolved: "pending" })[
      s?.toLowerCase()
    ] ?? "pending";

  const statusVariant = (s) =>
    ({ new: "new", pending: "pending", resolved: "resolved" })[
      s?.toLowerCase()
    ] ?? "secondary";

  const formatDate = (d) => {
    if (!d || d === "Unknown") return "—";
    try {
      return new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return d;
    }
  };

  const newCount = queries.filter(
    (q) => q.status.toLowerCase() === "new",
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Queries Management"
        description="View and manage contact form submissions from visitors"
      />

      <InfoBanner
        title="Query Management"
        description="Track and respond to visitor inquiries. New queries are highlighted and sorted first."
        capabilities={[
          "Search by name or email",
          "Filter by status",
          "One-click status updates",
          "Sorted by priority",
        ]}
      />

      {/* New queries alert */}
      {newCount > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-yellow-600 shrink-0" />
          <span className="text-sm font-medium text-yellow-800">
            {newCount} new {newCount === 1 ? "query" : "queries"} require
            attention
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Filter bar */}
      <FilterBar
        search={searchTerm}
        onSearch={(v) => {
          setSearchTerm(v);
        }}
        placeholder="Search by name or email…"
        count={pagination.total}
      >
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
          <SelectTrigger className="w-36 h-8 text-[13px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => fetchQueries(1)} disabled={loading}>
          Search
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setSearchTerm("");
            setStatusFilter("all");
            fetchQueries(1);
          }}
          disabled={loading}
        >
          Clear
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => fetchQueries(pagination.page)}
          disabled={loading}
          title="Refresh"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
          />
        </Button>
      </FilterBar>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-12 text-gray-400"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                    Loading queries…
                  </div>
                </TableCell>
              </TableRow>
            ) : queries.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-12 text-gray-400"
                >
                  No results found
                </TableCell>
              </TableRow>
            ) : (
              queries.map((q) => (
                <TableRow
                  key={q.id}
                  className={
                    q.status.toLowerCase() === "new"
                      ? "border-l-2 border-l-yellow-400"
                      : ""
                  }
                >
                  <TableCell className="font-medium text-gray-900">
                    {q.name}
                  </TableCell>
                  <TableCell className="text-gray-600">{q.email}</TableCell>
                  <TableCell className="text-gray-600">{q.mobile}</TableCell>
                  <TableCell className="max-w-[220px]">
                    <p
                      className="truncate text-gray-600 text-xs"
                      title={q.message}
                    >
                      {q.message}
                    </p>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(q.status)}>
                      {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-500 text-xs">
                    {formatDate(q.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {q.status.toLowerCase() !== "resolved" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            updateStatus(q.id, getNextStatus(q.status))
                          }
                          disabled={loading}
                        >
                          Mark{" "}
                          {getNextStatus(q.status).charAt(0).toUpperCase() +
                            getNextStatus(q.status).slice(1)}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(q.id, "pending")}
                          disabled={loading}
                        >
                          Reopen
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteId(q.id)}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchQueries(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
            >
              Previous
            </Button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 ||
                  p === pagination.total_pages ||
                  Math.abs(p - pagination.page) <= 1,
              )
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "..." ? (
                  <span key={i} className="px-2 text-gray-400 text-sm">
                    …
                  </span>
                ) : (
                  <Button
                    key={i}
                    size="sm"
                    variant={p === pagination.page ? "default" : "outline"}
                    onClick={() => fetchQueries(p)}
                    disabled={loading}
                  >
                    {p}
                  </Button>
                ),
              )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchQueries(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages || loading}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
      <ConfirmDialog
        open={!!deleteId}
        title="Delete query?"
        description="This action cannot be undone."
        onConfirm={deleteQuery}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />
    </div>
  );
};

export default QueriesCRUD;
